// Loads the DD JSON, normalizes it into a typed Set[] with computed fields:
// festival day, closer flag, badge extraction, canonical stage names.

import { vegasIsoToDate, festivalDay } from "../lib/time";
import { MAIN_STAGES, STAGE_COLORS } from "./stages";

export type FestSet = {
  id: string;                  // performanceId
  artist: string;              // raw "Porter Robinson (DJ Set)"
  artistDisplay: string;       // cleaned "Porter Robinson"
  badge?: string;              // "DJ SET" / "SUNRISE SET" / "THROWBACK SET"
  stage: string;               // canonical stage name
  stageColor: string;          // our palette
  day: 1 | 2 | 3;
  startUTC: Date;
  endUTC: Date;
  durationMin: number;
  isCloser: boolean;
  genres: string[];
};

const BADGE_TAGS: { tag: string; label: string }[] = [
  { tag: "(Sunrise Set)",   label: "SUNRISE" },
  { tag: "(Throwback Set)", label: "THROWBACK" },
  { tag: "(DJ Set)",        label: "DJ SET" },
];

function extractBadge(name: string): { display: string; badge?: string } {
  for (const { tag, label } of BADGE_TAGS) {
    if (name.includes(tag)) {
      return { display: name.replace(tag, "").trim(), badge: label };
    }
  }
  return { display: name };
}

let cache: FestSet[] | null = null;

export async function loadSchedule(): Promise<FestSet[]> {
  if (cache) return cache;
  const res = await fetch(import.meta.env.BASE_URL + "data/dd.json");
  const raw = await res.json();
  const perfs = raw.artistPerformanceList as any[];

  // Normalize all sets.
  const all: FestSet[] = perfs.map(p => {
    const start = vegasIsoToDate(p.startTime);
    const end = vegasIsoToDate(p.endTime);
    const stage = (p.stageName ?? "").trim();
    const { display, badge } = extractBadge(p.name);
    return {
      id: p.performanceId,
      artist: p.name,
      artistDisplay: display,
      badge,
      stage,
      stageColor: STAGE_COLORS[stage] ?? p.stageColor ?? "#444",
      day: festivalDay(start),
      startUTC: start,
      endUTC: end,
      durationMin: Math.round((end.getTime() - start.getTime()) / 60000),
      isCloser: false, // computed below
      genres: (p.genreList ?? []).map((g: any) => (typeof g === "string" ? g : g.name)).filter(Boolean),
    };
  });

  // Compute closers — last set per (day, stage).
  const closerKey = new Map<string, FestSet>();
  for (const s of all) {
    const key = `${s.day}|${s.stage}`;
    const cur = closerKey.get(key);
    if (!cur || s.endUTC > cur.endUTC) closerKey.set(key, s);
  }
  for (const c of closerKey.values()) c.isCloser = true;

  cache = all;
  return all;
}

export function setsForDay(all: FestSet[], day: 1 | 2 | 3): FestSet[] {
  return all.filter(s => s.day === day).sort((a, b) => a.startUTC.getTime() - b.startUTC.getTime());
}

export function setsForDayOnStages(all: FestSet[], day: 1 | 2 | 3, stages: string[]): FestSet[] {
  const stageSet = new Set(stages);
  return setsForDay(all, day).filter(s => stageSet.has(s.stage));
}

// Returns pairs of overlapping favorited sets.
export function findConflicts(faveSets: FestSet[]): [FestSet, FestSet][] {
  const sorted = [...faveSets].sort((a, b) => a.startUTC.getTime() - b.startUTC.getTime());
  const out: [FestSet, FestSet][] = [];
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      if (sorted[j].startUTC >= sorted[i].endUTC) break;
      out.push([sorted[i], sorted[j]]);
    }
  }
  return out;
}

export { MAIN_STAGES };
